import { Component, OnInit, inject } from '@angular/core';
import { AppService, FeatureFlags, LaunchDarklyService } from '@lc/core';
import { NavigationMenuItem } from '@lc/ui';
import { Observable, map, shareReplay } from 'rxjs';
import { MatDrawerContainer, MatDrawer } from '@angular/material/sidenav';
import { RouterOutlet } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { NavigationMenuComponent } from '@lc/ui/lib/components/navigation-menu/navigation-menu.component';

@Component({
  selector: 'lc-admin-navigation',
  templateUrl: './admin-navigation.component.html',
  styleUrls: ['./admin-navigation.component.scss'],
  imports: [MatDrawerContainer, MatDrawer, NavigationMenuComponent, RouterOutlet, AsyncPipe],
})
export class AdminNavigationComponent implements OnInit {
  private launchDarklyService = inject(LaunchDarklyService);

  public isExpanded = true;
  public hideNavBar = false;

  readonly tabs$: Observable<NavigationMenuItem[]>;

  constructor() {
    const isSuperAdmin$ = this.launchDarklyService
      .getFeature$(FeatureFlags.SUPER_ADMIN)
      .pipe(shareReplay(1));

    this.tabs$ = isSuperAdmin$.pipe(map((isSuperAdmin) => this.getTabs(isSuperAdmin)));
  }

  ngOnInit() {
    if (window.location.pathname.includes('/indesign/intake')) {
      this.hideNavBar = true;
    }
  }

  private getTabs(isSuperAdmin: boolean) {
    // Since we want don't want to mutate `this.defaultNavigationTabs`
    // we will use the spread operator to create a new reference.
    const tabs = [...this.defaultNavigationTabs];

    if (AppService.get('environment') !== 'prod') {
      tabs.push(new NavigationMenuItem('Video Orders', '/video-orders', 'video_library'));
    }

    if (isSuperAdmin) {
      tabs.push(
        new NavigationMenuItem('Store', '/store', 'store', [
          new NavigationMenuItem('Orders', '/store/orders', 'tag'),
          new NavigationMenuItem('Payments', '/store/payments', 'receipt'),
          new NavigationMenuItem('Customers', '/store/customers', 'contacts_product'),
          new NavigationMenuItem('Tax Codes', '/store/tax-codes', 'money_bag'),
        ]),
      );
    }

    return tabs;
  }

  // TODO: Tabs need to support nested routes
  private readonly defaultNavigationTabs: NavigationMenuItem[] = [
    new NavigationMenuItem('Dashboard', '/', 'home', [], { exactMatch: true }),
    new NavigationMenuItem('Product Catalog', '/product-catalog/products', 'sell', [
      new NavigationMenuItem('Products', '/product-catalog/products'),
      new NavigationMenuItem('Packages', '/product-catalog/packages'),
    ]),
    new NavigationMenuItem('Specs and Offerings', '/data-setup', 'settings', [
      new NavigationMenuItem('Specs and Offerings', '/data-setup', 'library_add'),
      new NavigationMenuItem('Template Intake', '/templates/pending', 'library_add'),
      new NavigationMenuItem(
        'Template Offering Intake',
        '/templates/offerings/pending',
        'library_add',
      ),
      new NavigationMenuItem('Offer Templates', '/templates/offer', 'search'),
    ]),
    new NavigationMenuItem('Photography', '/photography', 'camera_alt', [
      new NavigationMenuItem('Agencies', '/photography/agencies', 'photo_library'),
      new NavigationMenuItem('Service Offerings', '/photography/service-offerings', 'list'),
      new NavigationMenuItem('Vendors', '/photography/vendors', 'storefront'),
      new NavigationMenuItem('Global Price Regions', '/photography/regions', 'map'),
      new NavigationMenuItem('Releases', '/photography/releases', 'event'),
      new NavigationMenuItem('Lookup', '/photography/lookup', 'search'),
    ]),
    new NavigationMenuItem('Marketing Orders', '/orders', 'shopping_basket', [
      new NavigationMenuItem('Order Details', '/orders/details', 'list_alt'),
    ]),
    new NavigationMenuItem('Org Management', '/org-management', 'domain', [
      new NavigationMenuItem('Profiles', '/org-management/users', 'people_outline'),
      new NavigationMenuItem(
        'Company Exclusions',
        '/org-management/company-exclusions',
        'apartment',
      ),
    ]),
    new NavigationMenuItem('Email Templates', '/emailtemplates', 'email'),
    new NavigationMenuItem('Indesign Template', '/indesign/intake', 'library_add'),
    new NavigationMenuItem('Regenerate Pdf', '/regeneratepdf', 'search'),
    new NavigationMenuItem('Surveys', '/surveys', 'add_reaction', [
      new NavigationMenuItem('Manage Surveys', '/surveys', 'add_reaction', [], {
        exactMatch: true,
      }),
      new NavigationMenuItem('Reports', '/surveys/reporting', 'poll'),
    ]),
    new NavigationMenuItem('Reports', '/reporting', 'analytics', [
      new NavigationMenuItem(
        'Status Time Tracking',
        '/reporting/status-time-tracking',
        'schedule',
        [],
        {
          exactMatch: true,
        },
      ),
      new NavigationMenuItem('CBA Usage Report', '/reporting/cba-usage-report', 'schedule', [], {
        exactMatch: true,
      }),
    ]),
  ];
}
